// CHAT-SWITCH-B (docs/direction/2026-10-28-chat-slack-switch.md): the follow-ups of CHAT-SWITCH-A
// and the next must-haves that need no outside account.
//
//   messages.locate        a message link, however old: where the message is (channel, topic,
//                          thread, its root) and the root with its replies, read under the
//                          thread's own rule. A link no longer depends on the message being in
//                          the pages a client happens to have loaded.
//   chatAsks.*             "Ask" on the search page: a DE (the Chief of Staff by default, or one
//                          the person picks) answers from chat history. The messages it may read
//                          are found AS THE ASKER (their search, their channels) when the ask is
//                          made, and checked again against the asker when the DE reads them; the
//                          DE is woken through the usual wake-up and reads and answers through
//                          its run (`agentChatAsks.*`, the `get_chat_ask` / `answer_chat_ask` MCP
//                          tools). An answer may cite only those messages.
//   chatWebhooks.*         a channel's incoming webhooks (Slack's): its managers create one and
//                          get a secret URL once; a POST of `{text}` there posts in the channel
//                          as an Integration. Rotate, revoke, per-hook and per-company rates.
//   scheduledMessages.*    "Send later": the caller's messages waiting for their time, editable
//                          and cancellable until the `chat.scheduled_send` job posts them as them
//                          (with their access then, not when written).
//   messages.previews      the link cards of a message: Open Graph fetched by the server through
//                          the egress door (public addresses only, https, 5 s, 512 KB), cached
//                          per company and URL; `messages.hidePreview` lets the author remove one.
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { BlockNoteDoc } from "../work-rows";
import { MessageItemParams, MessageView, SearchHas, SearchIs } from "./chat";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const chat = { feature: "chat" } as const;

// Message links ----------------------------------------------------------------------------------

/** Replies `messages.locate` returns with the root, at most. */
export const LOCATE_MAX_REPLIES = 500;

export const MessageLocation = z
  .object({
    message_id: Uuid,
    thread_id: Uuid,
    channel_id: Uuid.nullable(),
    topic_id: Uuid.nullable(),
    /** The thread root the message belongs to (itself for a root). */
    root_id: Uuid,
    root: MessageView,
    /**
     * The root's replies, oldest first: a root's first `LOCATE_MAX_REPLIES`; for a reply, up to
     * that many around it (always including it), however long the thread.
     */
    replies: z.array(MessageView),
    replies_truncated: z.boolean(),
  })
  .meta({ id: "MessageLocation" });
export type MessageLocation = z.infer<typeof MessageLocation>;

// Ask ---------------------------------------------------------------------------------------------

/** Messages one ask may read, at most. */
export const ASK_MAX_SOURCES = 40;
/** An ask still working after this long reads as timed out. */
export const ASK_TIMEOUT_MS = 10 * 60_000;

export const ChatAskCreate = z
  .strictObject({
    question: z.string().trim().min(3).max(500),
    /** The DE to ask; absent: the Chief of Staff (409 when the company has none). */
    de_membership_id: Uuid.optional(),
    /** The search page's words and filters: the sources come from this search. */
    q: z.string().trim().min(1).max(200).optional(),
    from: Uuid.optional(),
    in: Uuid.optional(),
    has: SearchHas.optional(),
    is: SearchIs.optional(),
    before: z.iso.date().optional(),
    after: z.iso.date().optional(),
  })
  .meta({ id: "ChatAskCreate" });

/** A message an answer cites, as the asker may read it now. */
export const ChatAskCitation = z
  .object({
    message_id: Uuid,
    thread_id: Uuid,
    channel_id: Uuid,
    topic_id: Uuid,
    parent_message_id: Uuid.nullable(),
    /** `#channel › topic`, or the DM's name. */
    title: z.string(),
    snippet: z.string(),
    author_membership_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ChatAskCitation" });

export const ChatAsk = z
  .object({
    id: Uuid,
    question: z.string(),
    de_membership_id: Uuid,
    /** `working` (the DE has it), `answered`, `failed` (it could not, or did not in time). */
    state: z.enum(["working", "answered", "failed"]),
    answer: z.string().nullable(),
    /** The cited messages the asker can still read (one gone since is left out). */
    citations: z.array(ChatAskCitation),
    /** How many messages the DE was given. */
    source_count: z.int().min(0),
    failure: z.string().nullable(),
    created_at: Timestamp,
    answered_at: Timestamp.nullable(),
  })
  .meta({ id: "ChatAsk" });
export type ChatAsk = z.infer<typeof ChatAsk>;

/** What the asked DE reads (its run token): the question and the messages, as the asker sees. */
export const AgentChatAsk = z
  .object({
    id: Uuid,
    question: z.string(),
    asked_by: z.string(),
    state: z.enum(["working", "answered", "failed"]),
    instructions: z.string(),
    sources: z.array(
      z.object({
        message_id: Uuid,
        /** `#channel › topic`, or the DM's name. */
        place: z.string(),
        author: z.string(),
        created_at: Timestamp,
        text: z.string(),
      }),
    ),
  })
  .meta({ id: "AgentChatAsk" });

export const AgentChatAskAnswer = z
  .strictObject({
    answer: z.string().trim().min(1).max(8000),
    /** Messages of the ask's sources the answer rests on (any other id is refused). */
    cited_message_ids: z.array(Uuid).max(ASK_MAX_SOURCES),
  })
  .meta({ id: "AgentChatAskAnswer" });

// Incoming webhooks --------------------------------------------------------------------------------

/** Text a webhook may post (characters). */
export const WEBHOOK_TEXT_MAX = 4000;
/** Posts per hook per minute, and per company across its hooks. */
export const WEBHOOK_PER_MINUTE = 30;
export const WEBHOOK_COMPANY_PER_MINUTE = 300;
/** Hooks one channel may have (revoked ones not counted). */
export const WEBHOOKS_PER_CHANNEL = 10;

export const ChannelWebhook = z
  .object({
    id: Uuid,
    /** Where a post goes: a channel's topic, or a DE (its webhook DM with the creator; wakes it). */
    target: z.enum(["channel", "de"]),
    channel_id: Uuid.nullable(),
    de_membership_id: Uuid.nullable(),
    name: z.string(),
    /** The URL's last four characters (the URL itself is shown once). */
    token_hint: z.string(),
    created_by_membership_id: Uuid,
    post_count: z.int().min(0),
    last_used_at: Timestamp.nullable(),
    rotated_at: Timestamp.nullable(),
    revoked_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ChannelWebhook" });
export type ChannelWebhook = z.infer<typeof ChannelWebhook>;

export const ChannelWebhookList = z
  .object({ data: z.array(ChannelWebhook) })
  .meta({ id: "ChannelWebhookList" });

export const ChannelWebhookCreate = z
  .strictObject({ name: z.string().trim().min(1).max(80) })
  .meta({ id: "ChannelWebhookCreate" });

/** Create and rotate: the hook and its secret URL's path on the API, shown this once. */
export const ChannelWebhookSecret = z
  .object({ webhook: ChannelWebhook, path: z.string() })
  .meta({ id: "ChannelWebhookSecret" });

export const ChatWebhookParams = z
  .object({ co: Uuid, id: Uuid, token: z.string().min(16).max(128) })
  .meta({ id: "ChatWebhookParams" });

export const ChatWebhookPost = z
  .object({ text: z.string().trim().min(1).max(WEBHOOK_TEXT_MAX) })
  .meta({ id: "ChatWebhookPost" });

export const ChatWebhookPosted = z
  .object({ ok: z.literal(true), message_id: Uuid })
  .meta({ id: "ChatWebhookPosted" });

// Scheduled messages -------------------------------------------------------------------------------

/** How far ahead a message may be scheduled. */
export const SCHEDULE_MAX_AHEAD_MS = 120 * 86_400_000;
/** Messages one person may have waiting at once. */
export const SCHEDULED_PER_PERSON = 100;

export const ScheduledMessage = z
  .object({
    id: Uuid,
    thread_id: Uuid,
    channel_id: Uuid,
    topic_id: Uuid.nullable(),
    parent_message_id: Uuid.nullable(),
    body: BlockNoteDoc,
    body_text: z.string(),
    send_at: Timestamp,
    state: z.enum(["scheduled", "sent", "cancelled", "failed"]),
    message_id: Uuid.nullable(),
    failure: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ScheduledMessage" });
export type ScheduledMessage = z.infer<typeof ScheduledMessage>;

export const ScheduledMessageList = z
  .object({ data: z.array(ScheduledMessage) })
  .meta({ id: "ScheduledMessageList" });

const MentionRef = z
  .string()
  .max(80)
  .regex(/^(person|seat|team|de):[0-9a-f-]{36}$/);

/** `body` (BlockNote) or `body_markdown`, exactly one; `send_at` a minute to 120 days ahead. */
export const ScheduledMessageCreate = z
  .strictObject({
    thread_id: Uuid,
    body: BlockNoteDoc.optional(),
    body_markdown: z.string().min(1).max(20000).optional(),
    mentions: z.array(MentionRef).max(100).optional(),
    parent_message_id: Uuid.nullable().optional(),
    send_at: Timestamp,
    /** The request's own id: a resent Schedule with it returns the message already scheduled. */
    client_id: z.string().min(1).max(128).optional(),
    /** As on a send: the DE addressed (`none`: nobody), and `@channel` / `@here`. */
    addressee: z.union([Uuid, z.literal("none")]).optional(),
    broadcast: z.enum(["channel", "here"]).optional(),
  })
  .refine((m) => (m.body === undefined) !== (m.body_markdown === undefined), {
    message: "exactly one of body and body_markdown",
  })
  .meta({ id: "ScheduledMessageCreate" });

/**
 * A new text replaces the old one with everything it notifies: `body` (the editor's blocks, as
 * written: formatting and links kept) with its `mentions` and `broadcast`, or `body_markdown`
 * (which mentions nobody). Mentions and `@channel` no longer in the text never notify.
 */
export const ScheduledMessagePatch = z
  .strictObject({
    body: BlockNoteDoc.optional(),
    body_markdown: z.string().min(1).max(20000).optional(),
    mentions: z.array(MentionRef).max(100).optional(),
    broadcast: z.enum(["channel", "here"]).nullable().optional(),
    send_at: Timestamp.optional(),
  })
  .refine((p) => p.body === undefined || p.body_markdown === undefined, {
    message: "at most one of body and body_markdown",
  })
  .refine((p) => (p.mentions === undefined && p.broadcast === undefined) || p.body !== undefined, {
    message: "mentions and broadcast come with a new body",
  })
  .refine((p) => p.body !== undefined || p.body_markdown !== undefined || p.send_at !== undefined, {
    message: "nothing to change",
  })
  .meta({ id: "ScheduledMessagePatch" });

// Link previews ------------------------------------------------------------------------------------

/** Cards one message shows, at most (its first links). */
export const PREVIEWS_PER_MESSAGE = 3;

export const LinkPreview = z
  .object({
    url: z.string(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    image_url: z.string().nullable(),
    site_name: z.string().nullable(),
  })
  .meta({ id: "LinkPreview" });
export type LinkPreview = z.infer<typeof LinkPreview>;

export const LinkPreviewList = z
  .object({
    data: z.array(LinkPreview),
    /** The caller wrote the message (it may remove a card). */
    removable: z.boolean(),
    /** A card is still being fetched: ask again in a moment. */
    pending: z.boolean(),
  })
  .meta({ id: "LinkPreviewList" });

export const LinkPreviewHide = z
  .strictObject({ url: z.string().min(1).max(2048) })
  .meta({ id: "LinkPreviewHide" });

export const chatSwitchBRoutes = {
  "messages.locate": defineRoute({
    ...chat,
    id: "messages.locate",
    method: "get",
    path: `${base}/messages/{id}/locate`,
    tag: "messages",
    summary: "Where a message is, with its thread root and replies (a message link, however old)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: MessageLocation },
    errors: [],
    emits: [],
  }),

  "chatAsks.create": defineRoute({
    ...chat,
    id: "chatAsks.create",
    method: "post",
    path: `${base}/chat/asks`,
    tag: "search",
    summary: "Ask a DE (the Chief of Staff by default) to answer from the chat history I can read",
    access: "member",
    params: CompanyParams,
    body: ChatAskCreate,
    response: { status: 201, schema: ChatAsk },
    errors: ["conflict"],
    emits: ["chat_ask.requested", "wakeup.requested"],
  }),
  "chatAsks.get": defineRoute({
    ...chat,
    id: "chatAsks.get",
    method: "get",
    path: `${base}/chat/asks/{id}`,
    tag: "search",
    summary: "One of my asks: working, its answer with the messages it cites, or why it failed",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChatAsk },
    errors: [],
    emits: [],
  }),
  "agentChatAsks.get": defineRoute({
    ...chat,
    id: "agentChatAsks.get",
    method: "get",
    path: `${base}/agent/chat-asks/{id}`,
    tag: "search",
    summary:
      "get_chat_ask: the question put to me and the messages I may answer from (as the asker reads them)",
    access: "run_token",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentChatAsk },
    errors: [],
    emits: [],
  }),
  "agentChatAsks.answer": defineRoute({
    ...chat,
    id: "agentChatAsks.answer",
    method: "post",
    path: `${base}/agent/chat-asks/{id}/answer`,
    tag: "search",
    summary: "answer_chat_ask: answer the question, citing the messages the answer rests on",
    access: "run_token",
    params: CompanyItemParams,
    body: AgentChatAskAnswer,
    response: { status: 200, schema: ChatAsk },
    errors: ["conflict"],
    emits: ["chat_ask.answered"],
  }),

  "chatWebhooks.list": defineRoute({
    ...chat,
    id: "chatWebhooks.list",
    method: "get",
    path: `${base}/channels/{id}/webhooks`,
    tag: "channels",
    summary: "The channel's incoming webhooks (its managers)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChannelWebhookList },
    errors: [],
    emits: [],
  }),
  "chatWebhooks.create": defineRoute({
    ...chat,
    id: "chatWebhooks.create",
    method: "post",
    path: `${base}/channels/{id}/webhooks`,
    tag: "channels",
    summary: "Add an incoming webhook to the channel; its secret URL is shown once",
    access: "member",
    params: CompanyItemParams,
    body: ChannelWebhookCreate,
    response: { status: 201, schema: ChannelWebhookSecret },
    errors: ["conflict"],
    emits: ["chat_webhook.created"],
  }),
  "chatWebhooks.listForDe": defineRoute({
    ...chat,
    id: "chatWebhooks.listForDe",
    method: "get",
    path: `${base}/chat/des/{id}/webhooks`,
    tag: "channels",
    summary: "A DE's incoming webhooks (company admins)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChannelWebhookList },
    errors: [],
    emits: [],
  }),
  "chatWebhooks.createForDe": defineRoute({
    ...chat,
    id: "chatWebhooks.createForDe",
    method: "post",
    path: `${base}/chat/des/{id}/webhooks`,
    tag: "channels",
    summary: "Add an incoming webhook that wakes the DE; its secret URL is shown once",
    description:
      "Company admins. Each post lands in the DE's webhook DM with the admin who made it (so a " +
      "person sees what came in and what the DE did) and wakes the DE as a message addressed to " +
      "it would. The DE is told the text is untrusted outside data.",
    access: "member",
    params: CompanyItemParams,
    body: ChannelWebhookCreate,
    response: { status: 201, schema: ChannelWebhookSecret },
    errors: ["conflict"],
    // The DE's webhook DM with the admin is opened on the first hook.
    emits: ["chat_webhook.created", "channel.created", "topic.created"],
  }),
  "chatWebhooks.rotate": defineRoute({
    ...chat,
    id: "chatWebhooks.rotate",
    method: "post",
    path: `${base}/chat/webhooks/{id}/rotate`,
    tag: "channels",
    summary: "A new secret URL for the webhook (the old one stops at once)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChannelWebhookSecret },
    errors: ["conflict"],
    emits: ["chat_webhook.rotated"],
  }),
  "chatWebhooks.revoke": defineRoute({
    ...chat,
    id: "chatWebhooks.revoke",
    method: "post",
    path: `${base}/chat/webhooks/{id}/revoke`,
    tag: "channels",
    summary: "Turn the webhook off for good (its URL answers 404)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChannelWebhook },
    errors: [],
    emits: ["chat_webhook.revoked"],
  }),
  "chatWebhooks.receive": defineRoute({
    feature: "core",
    id: "chatWebhooks.receive",
    method: "post",
    path: `${base}/hooks/chat/{id}/{token}`,
    tag: "webhooks",
    summary:
      "Post `{text}` to the webhook's target (a channel, or a DE it wakes) as an Integration",
    description:
      "Called by the outside system: the secret URL is the credential (stored hashed). JSON " +
      "`{text}` (Markdown, at most 4,000 characters). An unknown, rotated-away or revoked URL, an " +
      "archived channel, a DE that left and chat switched off all answer 404. 30 posts a minute " +
      "per hook and 300 per company (429 with `Retry-After`).",
    access: "feed_token",
    params: ChatWebhookParams,
    body: ChatWebhookPost,
    response: { status: 200, schema: ChatWebhookPosted },
    errors: [],
    emits: ["topic.created", "message.created", "wakeup.requested"],
  }),

  "scheduledMessages.list": defineRoute({
    ...chat,
    id: "scheduledMessages.list",
    method: "get",
    path: `${base}/chat/scheduled`,
    tag: "messages",
    summary: "My scheduled messages (waiting, and failed ones from the last week)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ScheduledMessageList },
    errors: [],
    emits: [],
  }),
  "scheduledMessages.create": defineRoute({
    ...chat,
    id: "scheduledMessages.create",
    method: "post",
    path: `${base}/chat/scheduled`,
    tag: "messages",
    summary: "Send a message later (in a thread I may post in)",
    access: "member",
    params: CompanyParams,
    body: ScheduledMessageCreate,
    response: { status: 201, schema: ScheduledMessage },
    errors: ["conflict"],
    emits: ["scheduled_message.created"],
  }),
  "scheduledMessages.update": defineRoute({
    ...chat,
    id: "scheduledMessages.update",
    method: "patch",
    path: `${base}/chat/scheduled/{id}`,
    tag: "messages",
    summary: "Change a waiting message's text or time",
    access: "member",
    params: CompanyItemParams,
    body: ScheduledMessagePatch,
    response: { status: 200, schema: ScheduledMessage },
    errors: ["conflict"],
    emits: ["scheduled_message.updated"],
  }),
  "scheduledMessages.cancel": defineRoute({
    ...chat,
    id: "scheduledMessages.cancel",
    method: "post",
    path: `${base}/chat/scheduled/{id}/cancel`,
    tag: "messages",
    summary: "Cancel a waiting message",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ScheduledMessage },
    errors: ["conflict"],
    emits: ["scheduled_message.cancelled"],
  }),

  "messages.previews": defineRoute({
    ...chat,
    id: "messages.previews",
    method: "get",
    path: `${base}/threads/{id}/messages/{mid}/previews`,
    tag: "messages",
    summary: "The link cards of a message (Open Graph, fetched and cached by the server)",
    access: "member",
    params: MessageItemParams,
    response: { status: 200, schema: LinkPreviewList },
    errors: [],
    emits: [],
  }),
  "messages.hidePreview": defineRoute({
    ...chat,
    id: "messages.hidePreview",
    method: "post",
    path: `${base}/threads/{id}/messages/{mid}/previews/hide`,
    tag: "messages",
    summary: "Remove a link card from my message",
    access: "member",
    params: MessageItemParams,
    body: LinkPreviewHide,
    response: { status: 200, schema: LinkPreviewList },
    errors: [],
    emits: ["message.preview_hidden"],
  }),
};
